import { FC, useCallback, useEffect, useState } from "react";
import { GridApi } from "ag-grid-community";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from "@mui/material";
import { useDialog } from "@/utils/hooks/useDialog.hook";
import { SavingsOpportunitiesGroupingOptionsType } from "@/components/pages/savings-opportunities/utils/types/types";

interface SavingsOpportunitiesGroupingSelectProps {
  gridApi: GridApi | null;
}

export const SavingsOpportunitiesGroupingSelect: FC<
  SavingsOpportunitiesGroupingSelectProps
> = ({ gridApi }) => {
  const [selectedValues, setSelectedValues] = useState<string[]>(["none"]);
  const { open, openDialog, closeDialog } = useDialog();

  const openHandler = useCallback(() => {
    openDialog();
    setSelectedValues((selectedValues) =>
      selectedValues.includes("none") ? [] : selectedValues,
    );
  }, [openDialog]);

  const closeHandler = useCallback(() => {
    closeDialog();
    if (!selectedValues.length) {
      setSelectedValues(["none"]);
    }
  }, [closeDialog, selectedValues]);

  const changeHandler = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const values = event.target.value as string[];

      if (values.includes("none")) {
        gridApi?.setRowGroupColumns([]);
        setSelectedValues(["none"]);
        closeDialog();
        return;
      }

      gridApi?.setRowGroupColumns(values);
      setSelectedValues(values);
    },
    [gridApi, closeDialog],
  );

  useEffect(() => {
    gridApi?.addEventListener("columnRowGroupChanged", (event) => {
      if (event.type !== "columnRowGroupChanged") {
        return;
      }

      const groupings =
        event?.api?.getRowGroupColumns()?.map((col) => col.getColId()) ?? [];

      setSelectedValues(groupings?.length ? groupings : ["none"]);
    });

    const groupings =
      gridApi?.getRowGroupColumns()?.map((col) => col.getColId()) ?? [];

    setSelectedValues(groupings?.length ? groupings : ["none"]);
  }, [gridApi]);

  return (
    <FormControl size="xsmall" sx={{ width: 150 }}>
      <InputLabel>Group By</InputLabel>

      <Select
        open={open}
        size="small"
        multiple={true}
        label="Group By"
        onOpen={openHandler}
        onClose={closeHandler}
        value={selectedValues}
        onChange={changeHandler}
        renderValue={renderValue}
        MenuProps={selectMenuProps}
      >
        <MenuItem value="none">None</MenuItem>

        {Object.entries(savingsOpportunitiesGroupingOptions).map(
          ([value, label]) => {
            const selectedIndex = selectedValues.findIndex(
              (val) => val === value,
            );
            const selected = selectedIndex > -1;

            return (
              <MenuItem value={value} key={value}>
                <ListItemIcon>
                  <Checkbox
                    size="small"
                    checked={selected}
                    sx={{ p: 0, pr: 1 }}
                  />
                </ListItemIcon>
                <ListItemText sx={{ pr: 3 }}>{label}</ListItemText>

                {selected && (
                  <Typography
                    variant="body2"
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: "50%",
                      color: "primary.main",
                      bgcolor: "primary.light",
                    }}
                  >
                    {selectedIndex + 1}
                  </Typography>
                )}
              </MenuItem>
            );
          },
        )}
      </Select>
    </FormControl>
  );
};

const selectMenuProps: SelectProps["MenuProps"] = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: 5, horizontal: "left" },
};

export const savingsOpportunitiesGroupingOptions = {
  opportunity_name: "Opportunity Name",
  action_type: "Opportunity Type",
  description: "Description",
  cloud_provider: "Cloud Provider",
  service: "Service",
  resource_type: "Resource Type",
  recommended_type: "Recommended Type",
  resource_id: "Resource ID/Name",
  resource_arn: "Resource ARN",
  implementation_effort: "Implementation Effort",
  based_on_past: "Based On Past",
  region: "Region",
  "account.provider_account_id": "Account",
};

const renderValue = (selectedValues: string[]) => {
  const firstSelected = selectedValues.at(
    0,
  ) as SavingsOpportunitiesGroupingOptionsType;

  const firstLabel = savingsOpportunitiesGroupingOptions[firstSelected];

  if (selectedValues.length > 1) {
    return `${firstLabel} + ${selectedValues.length - 1}`;
  }

  return firstLabel ?? "None";
};
